import React from 'react';
import { Box, Typography } from '@mui/material';
import { Bolt as FlashIcon, HourglassEmpty as WaitIcon } from '@mui/icons-material';
import { INSTA_QUIZ } from './instaQuizTheme';

const pulseDot = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  bgcolor: '#4ade80',
  flexShrink: 0,
  boxShadow: '0 0 8px rgba(74,222,128,0.8)',
  animation: 'liveDotPulse 1.5s ease-in-out infinite',
  '@keyframes liveDotPulse': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.6, transform: 'scale(0.85)' },
  },
};

const StatusBadge = ({ variant, label }) => {
  const isLive = variant === 'live';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 2,
        py: 0.85,
        borderRadius: '999px',
        maxWidth: '100%',
        bgcolor: isLive ? 'rgba(76,175,80,0.15)' : 'rgba(156,39,176,0.12)',
        border: isLive
          ? '1px solid rgba(129,199,132,0.45)'
          : `1px solid ${INSTA_QUIZ.cardBorder}`,
        animation: isLive ? 'liveBadgeGlow 2s ease-in-out infinite' : 'none',
        '@keyframes liveBadgeGlow': {
          '0%, 100%': { boxShadow: '0 0 0 rgba(74,222,128,0)' },
          '50%': { boxShadow: '0 0 12px rgba(74,222,128,0.25)' },
        },
      }}
    >
      {isLive ? (
        <Box sx={pulseDot} />
      ) : (
        <WaitIcon sx={{ fontSize: 16, color: INSTA_QUIZ.purpleLight, flexShrink: 0 }} />
      )}
      <Typography
        component="span"
        sx={{
          fontSize: { xs: '0.65rem', sm: '0.72rem' },
          fontWeight: 700,
          letterSpacing: { xs: 1, sm: 1.4 },
          textTransform: 'uppercase',
          color: isLive ? '#81C784' : INSTA_QUIZ.purpleLight,
          lineHeight: 1.2,
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const InstaQuizHeroHeader = ({
  variant = 'live',
  title,
  subtitle,
  children,
}) => {
  const isLive = variant === 'live';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        textAlign: 'center',
        mb: 4,
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 96,
          height: 96,
          borderRadius: '50%',
          mb: 2,
          flexShrink: 0,
          background: isLive
            ? 'linear-gradient(135deg, rgba(156,39,176,0.35) 0%, rgba(224,64,251,0.2) 100%)'
            : 'linear-gradient(135deg, rgba(156,39,176,0.2) 0%, rgba(100,100,120,0.15) 100%)',
          border: isLive
            ? `2px solid ${INSTA_QUIZ.cardBorder}`
            : '2px dashed rgba(156,39,176,0.45)',
          boxShadow: isLive ? '0 0 48px rgba(156,39,176,0.35)' : 'none',
          animation: isLive ? 'none' : 'waitPulse 2.5s ease-in-out infinite',
          '@keyframes waitPulse': {
            '0%, 100%': { opacity: 0.85, transform: 'scale(1)' },
            '50%': { opacity: 1, transform: 'scale(1.04)' },
          },
        }}
      >
        {isLive ? (
          <FlashIcon sx={{ fontSize: 48, color: INSTA_QUIZ.purpleLight }} />
        ) : (
          <WaitIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.4)' }} />
        )}
      </Box>

      {/* Badge — centered row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          mb: 2,
          px: 1,
        }}
      >
        <StatusBadge
          variant={variant}
          label={isLive ? 'Live Now' : 'Live Round Coming Soon'}
        />
      </Box>

      {/* Title */}
      <Typography
        variant="h3"
        sx={{
          color: '#fff',
          fontWeight: 800,
          mb: 1.5,
          fontSize: { xs: '1.75rem', sm: '2.35rem' },
          lineHeight: 1.15,
          textShadow: isLive ? '0 0 40px rgba(156,39,176,0.35)' : 'none',
          px: 1,
        }}
      >
        {title}
      </Typography>

      {/* Subtitle */}
      {subtitle && (
        <Typography
          variant="body1"
          sx={{
            color: INSTA_QUIZ.textMuted,
            maxWidth: 520,
            mx: 'auto',
            lineHeight: 1.75,
            fontSize: { xs: '0.95rem', sm: '1.05rem' },
            px: 1,
          }}
        >
          {subtitle}
        </Typography>
      )}

      {children}
    </Box>
  );
};

export default InstaQuizHeroHeader;
